import { getApiClient } from '@/lib/api/api-client';
import type { Physicalartwork } from '@/types';
import type {
  BaseQueryParams,
  PaginatedResponse,
  CatalogItem,
  FindInstallationsParams,
  WhereFilter,
} from './catalog-types';
import { getSortValue } from './catalog-types';
import { CacheService } from '../cache';
import { physicalTransformer } from '@/lib/transformers/physicals.transformer';
import { handleApiError, success, type ApiResult } from '@/lib/utils/errorHandler';
import { logger } from '@/lib/utils/logger';
import { getCacheDuration } from '@/lib/config/cache.config';

export function parseSearchParams(params: URLSearchParams): Partial<BaseQueryParams> {
  return {
    page: params.get('page') ? parseInt(params.get('page')!) : undefined,
    limit: params.get('limit') ? parseInt(params.get('limit')!) : 10,
    query: params.get('query') || undefined,
    depth: params.get('depth') ? parseInt(params.get('depth')!) : undefined,
    sort: params.get('sort') || undefined,
  };
}

/**
 * Transform Physicalartwork to CatalogItem
 */
export function physicalToCatalogItem(physical: Physicalartwork): CatalogItem {
  return physicalTransformer.toCatalogItem(physical);
}

/**
 * API service for Physicals
 */
export class PhysicalsAPI {
  static apiClient = getApiClient();
  static CACHE_DURATION = getCacheDuration('physicals'); // From centralized config

  /**
   * Get physical artwork list
   */
  static async findPhysicals(
    params: FindInstallationsParams & BaseQueryParams
  ): Promise<ApiResult<PaginatedResponse<CatalogItem>>> {
    const { page = 1, limit = 10, query, sort, years, ...rest } = params;

    const sortValue = getSortValue(sort, { dateField: 'updatedAt' });

    // Build where query for Payload CMS v3
    const where: WhereFilter = {};

    // Search query
    if (query) {
      where.title = {
        contains: query,
      };
    }

    // Year filter - use year field directly for Physicalartwork
    if (years && years.length > 0) {
      try {
        if (years.length === 1) {
          // Single year selected, use equals
          where.year = {
            equals: Number(years[0]),
          };
        } else {
          // Multiple years, use in operator
          where.year = {
            in: years.map(year => Number(year)),
          };
        }
      } catch (error) {
        logger.error('Error processing years:', error);
      }
    }

    // Additional where parameters
    if (rest.where) {
      Object.assign(where, rest.where);
    }

    logger.debug('Physicals API where query:', where);

    try {
      const response = await PhysicalsAPI.apiClient.collections.physicalartworks.find({
        page,
        limit,
        where,
        depth: params.depth || 2,
        // TODO: type properly - getSortValue returns dynamic strings not in payload-rest-client's keyof DOC union
        sort: sortValue as never,
      });

      // Transform API response to Physicalartwork type
      const result = response as PaginatedResponse<Physicalartwork>;

      // Check totalPages and page values
      if (result.totalDocs && result.limit) {
        const totalPages = Math.ceil(result.totalDocs / result.limit);
        result.totalPages = totalPages;

        // If current page is less than total pages, there are more pages
        result.hasNextPage = result.page < totalPages;

        // Process results before type conversion
        const catalogItems = result.docs.map(physicalToCatalogItem);

        logger.debug(
          `API response calculated: Page ${result.page}/${totalPages}, hasNextPage: ${result.hasNextPage}, totalDocs: ${result.totalDocs}, limit: ${result.limit}`
        );

        // Return result as new object to avoid type mismatch
        return success({
          ...result,
          docs: catalogItems,
        } as PaginatedResponse<CatalogItem>);
      } else {
        // Default to false if totalDocs or limit is missing
        result.hasNextPage = false;
        logger.debug(`API response calculated: hasNextPage: false (missing totalDocs or limit)`);

        // Convert empty docs array to CatalogItem array
        return success({
          ...result,
          docs: [],
        } as PaginatedResponse<CatalogItem>);
      }
    } catch (error) {
      return handleApiError('findPhysicals', error);
    }
  }

  /**
   * Get physical artwork detail by slug
   */
  static async findPhysicalBySlug(slug: string, skipCache: boolean = false): Promise<ApiResult<Physicalartwork>> {
    const cacheKey = await CacheService.scopedKey(`physical.service_${slug}`);
    // Check cache (if skipCache is false)
    if (!skipCache) {
      const cachedData = CacheService.getFromCache(cacheKey);
      if (cachedData) {
        return success(cachedData as Physicalartwork);
      }
    }
    try {
      const response = await PhysicalsAPI.apiClient.collections.physicalartworks.find({
        where: {
          slug: {
            equals: slug,
          },
        },
        depth: 2,
        limit: 1,
      });

      const physical = response.docs.length > 0 ? response.docs[0] : null;

      if (physical) {
        // Write to cache
        CacheService.addToCache(cacheKey, physical, PhysicalsAPI.CACHE_DURATION);
      }

      return success(physical);
    } catch (error) {
      return handleApiError('findPhysicalBySlug', error);
    }
  }
}
